<template>
  <v-card elevation="2" class="admin-card">
    <v-card-title class="card-title-row">
      <span>Roluri Agenți (Hunter / Farmer)</span>
      <v-btn icon color="success" size="small" variant="elevated" @click="openDialog()" title="Adaugă Rol Agent">
        <v-icon size="20">mdi-plus</v-icon>
      </v-btn>
    </v-card-title>
    <v-card-text class="pa-0">
      <v-table class="admin-table">
        <thead>
          <tr>
            <th>Utilizator</th>
            <th>Tip Agent</th>
            <th>Data Start</th>
            <th>Data End</th>
            <th>Status</th>
            <th>Acțiuni</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td colspan="6" class="text-center py-4">
              <v-progress-circular indeterminate :color="APP_Color" size="24" />
            </td>
          </tr>
          <tr v-else-if="agenti.length === 0">
            <td colspan="6" class="text-center py-4 text-grey">Niciun agent configurat.</td>
          </tr>
          <tr v-else v-for="agent in agenti" :key="agent.ID">
            <td><strong>{{ agent.Utilizator }}</strong></td>
            <td>
              <v-chip :color="agent.TipAgent === 'Hunter' ? 'deep-orange' : 'green'" size="x-small">
                {{ agent.TipAgent }}
              </v-chip>
            </td>
            <td>{{ formatDate(agent.DataStart) }}</td>
            <td>{{ formatDate(agent.DataEnd) || '—' }}</td>
            <td>
              <v-chip :color="isActiv(agent) ? 'success' : 'default'" size="x-small" variant="tonal">
                {{ isActiv(agent) ? 'Activ' : 'Inactiv' }}
              </v-chip>
            </td>
            <td>
              <v-btn icon size="x-small" variant="text" @click="openDialog(agent)">
                <v-icon size="16">mdi-pencil</v-icon>
              </v-btn>
              <v-btn icon size="x-small" variant="text" color="error" @click="deleteAgent(agent)">
                <v-icon size="16">mdi-delete</v-icon>
              </v-btn>
            </td>
          </tr>
        </tbody>
      </v-table>
    </v-card-text>
  </v-card>

  <v-dialog v-model="dialog" max-width="480">
    <v-card class="popup-card-modern">
      <div class="popup-header">
        <div class="popup-header-main">
          <v-icon class="popup-header-icon mr-3" color="primary">mdi-account-plus</v-icon>
          <div class="popup-header-texts">
            <div class="popup-header-title">{{ form.ID ? 'Editare' : 'Adăugare' }} Rol Agent</div>
            <div class="popup-header-subtitle">Configurați responsabilitățile agentului în sistem</div>
          </div>
        </div>
        <div class="popup-header-meta">
          <span class="status-chip status-chip--draft">{{ form.ID ? 'Modificare' : 'Nou' }}</span>
        </div>
      </div>

      <v-card-text class="popup-body-modern px-6 py-4">
        <v-row dense>
          <v-col cols="12">
            <v-autocomplete v-model="form.Utilizator" :items="utilizatoriDisponibili" item-title="Username" item-value="Username"
              label="Utilizator" density="compact" variant="outlined" clearable
              :loading="loadingUtilizatori" no-data-text="Niciun utilizator disponibil" />
          </v-col>
          <v-col cols="12">
            <v-select v-model="form.TipAgent" :items="['Hunter', 'Farmer']" label="Tip Agent" density="compact"
              variant="outlined" />
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field v-model="form.DataStart" label="Data Start" type="date" density="compact"
              variant="outlined" />
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field v-model="form.DataEnd" label="Data End (gol = activ indefinit)" type="date"
              density="compact" variant="outlined" clearable />
          </v-col>
        </v-row>
      </v-card-text>

      <div class="form-footer footer-modern px-6 py-3">
        <div class="footer-right">
          <v-btn variant="outlined" color="success" class="mr-2" @click="save" :loading="saving"
            style="text-transform: none; font-weight: 600;">Salvează</v-btn>
          <v-btn variant="outlined" color="error" @click="dialog = false"
            style="text-transform: none; font-weight: 600;">Închide</v-btn>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import AppSettings from '@/settings/appSettings'
import CrmKPIService from '@/services/Comercial/CrmKPI'
import AUTH_Users from '@/services/AUTH/AUTH_Users'
import notify from 'devextreme/ui/notify'
import { confirm } from 'devextreme/ui/dialog'

const emptyForm = () => ({
  ID: null as string | null,
  Utilizator: '',
  TipAgent: 'Hunter',
  DataStart: new Date().toISOString().slice(0, 10),
  DataEnd: '',
})

export default defineComponent({
  name: 'GridKPIAgenti',

  data() {
    return {
      APP_Color: AppSettings.APP_Color,
      agenti: [] as any[],
      loading: false,
      dialog: false,
      saving: false,
      form: emptyForm(),
      utilizatori: [] as any[],
      loadingUtilizatori: false,
    }
  },

  computed: {
    utilizatoriDisponibili(): any[] {
      // La editare: exclude toți agenții existenți MINUS cel curent (să rămână selectabil)
      // La adăugare: exclude toți utilizatorii care au deja un rol de agent
      const ocupati = new Set(
        this.agenti
          .filter((a: any) => a.Utilizator !== this.form.Utilizator)
          .map((a: any) => a.Utilizator)
      )
      return this.utilizatori.filter((u: any) => !ocupati.has(u.Username))
    },
  },

  mounted() {
    this.incarca()
    this.incarcaUtilizatori()
  },

  methods: {
    async incarcaUtilizatori() {
      this.loadingUtilizatori = true
      try {
        const res = await AUTH_Users.get()
        this.utilizatori = (res.data || []).sort((a: any, b: any) =>
          (a.Username || '').localeCompare(b.Username || ''))
      } catch (e) { console.error(e) } finally { this.loadingUtilizatori = false }
    },

    async incarca() {
      this.loading = true
      try {
        const res = await CrmKPIService.getAgenti({ '$orderby': 'Utilizator asc,DataStart desc' })
        this.agenti = res.data?.value || []
      } catch (e) { console.error(e) } finally { this.loading = false }
    },

    openDialog(item?: any) {
      this.form = item
        ? { ...item, DataStart: item.DataStart?.slice(0, 10) || '', DataEnd: item.DataEnd?.slice(0, 10) || '' }
        : emptyForm()
      this.dialog = true
    },

    async save() {
      this.saving = true
      try {
        const payload: any = {
          Utilizator: this.form.Utilizator,
          TipAgent: this.form.TipAgent,
          DataStart: this.form.DataStart || null,
          DataEnd: this.form.DataEnd || null,
        }
        if (this.form.ID) {
          await CrmKPIService.updateAgent(this.form.ID!, payload)
        } else {
          await CrmKPIService.saveAgent(payload)
        }
        this.dialog = false
        await this.incarca()
        notify('Date individuale salvate cu succes!', 'success', 2000)
      } catch (e) {
        console.error(e)
        notify('Eroare la salvarea datelor!', 'error', 2000)
      } finally { this.saving = false }
    },

    async deleteAgent(item: any) {
      const result = await confirm(`Sigur doriți să ștergeți rolul "${item.TipAgent}" pentru "${item.Utilizator}"?`, "Confirmare Ștergere")
      if (!result) return
      try {
        await CrmKPIService.deleteAgent(item.ID)
        await this.incarca()
        notify('Rolul a fost șters cu succes!', 'success', 2000)
      } catch (e) {
        console.error(e)
        notify('Eroare la ștergerea rolului!', 'error', 2000)
      }
    },

    isActiv(agent: any): boolean {
      const today = new Date()
      const start = agent.DataStart ? new Date(agent.DataStart) : null
      const end = agent.DataEnd ? new Date(agent.DataEnd) : null
      return (!start || start <= today) && (!end || end >= today)
    },

    formatDate(val: string | null | undefined): string {
      if (!val) return ''
      return new Date(val).toLocaleDateString('ro-RO')
    },
  }
})
</script>

<style scoped>
.admin-card {
  border-radius: 16px !important;
}

.card-title-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e0e0e0;
  font-weight: 600;
  font-size: 1.1rem;
}

.admin-table th {
  font-size: 0.75rem !important;
  text-transform: uppercase !important;
  letter-spacing: 0.05em !important;
  color: #718096 !important;
  font-weight: 600 !important;
  padding: 0.75rem 1.25rem !important;
  border-bottom: 2px solid #edf2f7 !important;
}

.admin-table td {
  padding: 0.75rem 1.25rem !important;
  font-size: 0.875rem !important;
  border-bottom: 1px solid #edf2f7 !important;
}

/* ── Modern Popup Styling ── */
.popup-card-modern {
  border-radius: 16px !important;
  overflow: hidden;
}

.popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  background: #f8fafc;
  border-bottom: 1px solid #edf2f7;
}

.popup-header-main {
  display: flex;
  align-items: center;
}

.popup-header-title {
  font-weight: 700;
  font-size: 1.1rem;
  color: #1e293b;
  line-height: 1.2;
}

.popup-header-subtitle {
  font-size: 0.8rem;
  color: #64748b;
  margin-top: 2px;
}

.status-chip {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.status-chip--draft {
  background: #fef3c7;
  color: #92400e;
}

.footer-modern {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  border-top: 1px solid #edf2f7;
  background: #ffffff;
}
</style>
